import { Info } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  DialogContentText,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import pluralize from "pluralize";
import { FC, useEffect, useMemo, useState } from "react";
import { AccountTypeAPI, OrganizationRoleAPI } from "../../../api";
import Dialog from "../../../components/Dialog";
import useFeature from "../../../hooks/useFeature";
import { useAlert } from "../../../lib/alert";
import keyify from "../../../lib/keyify";
import {
  AccountRegistrationType,
  AccountType,
} from "../../../model/AccountType";
import FieldDefinition from "../../../model/FieldDefinition";
import { OrganizationRole } from "../../../model/OrganizationRole";

export type EditAccountTypeDialogProps = {
  open: boolean;
  organizationId: string;
  accountType?: AccountType;
  accountId?: string;
  onClose?: () => void;
  onSave?: (accountType: AccountType) => void;
  save?: (accountType: AccountType) => Promise<AccountType>;
  title?: string;
  confirmText?: string;
};
const saveAccountType = (accountType: AccountType): Promise<AccountType> => {
  return AccountTypeAPI.update(accountType);
};
const EditAccountTypeDialog: FC<EditAccountTypeDialogProps> = ({
  title = "Edit Account Type",
  confirmText = "Save",
  accountType,
  save = saveAccountType,
  ...props
}) => {
  const { handleRejectionWithError } = useAlert();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [orgRoleId, setOrgRoleId] = useState<string | undefined>("");
  const [registrationType, setRegistrationType] = useState(
    AccountRegistrationType.InviteOnly,
  );
  const [fieldDefinitionOptions, setFieldDefinitionOptions] = useState<
    FieldDefinition[]
  >([]);
  const [selectedFieldDefinitions, setSelectedFieldDefinitions] = useState<
    FieldDefinition[]
  >([]);
  const [organizationRoles, setOrganizationRoles] = useState<
    OrganizationRole[]
  >([]);

  const canManageOrganizationRoles = useFeature("organizationRoles");

  const currentOrganizationRole = useMemo(() => {
    if (!accountType || !accountType.org_role_id) {
      return undefined;
    }
    return organizationRoles.find(
      (role) => role.id === accountType?.org_role_id,
    );
  }, [accountType, organizationRoles]);

  useEffect(() => {
    if (!canManageOrganizationRoles) return;

    let isMounted = true;
    OrganizationRoleAPI.list().then((roles) => {
      if (isMounted) setOrganizationRoles(roles);
    });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accountType) {
      setName(accountType.name);
      setDescription(accountType.description ?? "");
      setRegistrationType(
        accountType.registration_type ?? AccountRegistrationType.InviteOnly,
      );
      AccountTypeAPI.getFieldDefinitions(accountType.id).then((result) => {
        setFieldDefinitionOptions(Object.values(result.available));
        setSelectedFieldDefinitions(
          result.selected.map((id) => result.available[id]),
        );
      });
    }
  }, [accountType]);

  return (
    <Dialog
      open={props.open}
      title={title}
      confirmText={confirmText}
      cancelText={"Cancel"}
      maxWidth="xs"
      fullWidth={true}
      handleConfirm={(): Promise<void> => {
        if (!props.organizationId) {
          return Promise.resolve();
        }

        return save({
          id: accountType?.id ?? "",
          organization_id: props.organizationId,
          name: pluralize.singular(name),
          key: keyify(pluralize.singular(name)),
          registration_type: registrationType,
          org_role_id: orgRoleId,
          description,
        }).then((accountType) => {
          AccountTypeAPI.setSelectedFieldDefinitions(
            accountType.id,
            selectedFieldDefinitions.map((f) => f.id),
          ).then(() => {
            props.onClose && props.onClose();
            props.onSave && props.onSave(accountType);
          }, handleRejectionWithError("Failed to update field definitions"));
        }, handleRejectionWithError("Failed to save account type"));
      }}
      handleCancel={function (): void {
        props.onClose && props.onClose();
      }}
    >
      <DialogContentText>
        Enter a name for the type of accounts
      </DialogContentText>
      <TextField
        autoFocus
        margin="dense"
        id="name"
        label="Name"
        type="text"
        fullWidth
        variant="standard"
        value={name}
        onChange={(e) => setName(e.target.value as string)}
      />
      <TextField
        id="registration-type"
        value={registrationType}
        label="Registration Type"
        onChange={(e) =>
          setRegistrationType(e.target.value as AccountRegistrationType)
        }
        select
        fullWidth
        variant="standard"
        sx={{ marginTop: "5px" }}
      >
        <MenuItem value={AccountRegistrationType.InviteOnly}>
          Invite Only
        </MenuItem>
        <MenuItem value={AccountRegistrationType.SelfRegistration}>
          Self-Registration
        </MenuItem>
      </TextField>
      <TextField
        margin="dense"
        id="description"
        label="Description"
        type="text"
        fullWidth
        rows={3}
        multiline={true}
        variant="standard"
        value={description}
        onChange={(e) => setDescription(e.target.value as string)}
      />
      {canManageOrganizationRoles && (
        <Autocomplete
          options={organizationRoles}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Link to Organization Role"
              variant="standard"
              placeholder="No Link"
            />
          )}
          value={currentOrganizationRole}
          sx={{ marginTop: "5px" }}
          onChange={(e, newValue) => {
            if (newValue) {
              setOrgRoleId(newValue.id);
            } else {
              setOrgRoleId(undefined);
            }
          }}
        />
      )}
      <Box display={"flex"} alignItems={"center"}>
        <Autocomplete
          multiple
          options={fieldDefinitionOptions}
          value={selectedFieldDefinitions}
          onChange={(e, newValues) =>
            setSelectedFieldDefinitions([...newValues])
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.label ? option.label : "<Empty>"}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Field Definitions"
            />
          )}
          getOptionLabel={(option) => option.label ?? "Field Definition"}
          limitTags={3}
          sx={{ marginTop: "5px", flexGrow: 1 }}
        />
        <Tooltip
          sx={{
            maxWidth: "400px",
          }}
          title={
            <Box p={1} textAlign={"center"}>
              <Typography mb={0.5} variant="body1" fontWeight={600}>
                Field Definitions
              </Typography>
              <Typography variant="caption" lineHeight={1.2}>
                Field Definitions are built to collect additional information,
                autocomplete forms, and store data alongside accounts, users,
                and organizations. Any Field Definition associated with this
                Account Type may enable auto-filling of data based on previous
                entries.
              </Typography>
            </Box>
          }
        >
          <IconButton>
            <Info />
          </IconButton>
        </Tooltip>
      </Box>
    </Dialog>
  );
};

export default EditAccountTypeDialog;
